<script setup>
import { useModalogStore } from "@/stores/modalog.js";
import BrandModalogShell from "@/Components/Modalogs/BrandModalogShell.vue";
import BrandButton from "@/Components/Brand/BrandButton.vue";

/**
 * Shared chrome for brand confirmation modalogs (GH-965): the
 * BrandModalogShell with a body slot and the standard footer — cancel
 * link plus a BrandButton CTA with the GH-934 loading throbber.
 * Destructive confirms (`danger`) use the orange-red CTA from the
 * Delete Account flow. Cancel, backdrop, X and ESC all just close.
 */
const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    confirmLabel: {
        type: String,
        default: "Confirm",
    },
    // Progressive verb shown while busy ("Deleting", "Saving", …); falls
    // back to confirmLabel. The throbber dots carry the ellipsis (GH-1001).
    busyLabel: {
        type: String,
        default: null,
    },
    cancelLabel: {
        type: String,
        default: "Cancel",
    },
    danger: {
        type: Boolean,
        default: false,
    },
    // Working state: throbs the CTA and blocks a second submit.
    busy: {
        type: Boolean,
        default: false,
    },
    // Gates the CTA until the modalog's input is valid (dimmed, unclickable).
    confirmDisabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["confirm"]);

const modalogStore = useModalogStore();
</script>

<template>
    <!-- Stable root + hasModalog gate, per the brand-modalog pattern. -->
    <div>
        <BrandModalogShell v-if="modalogStore.hasModalog" :title="title">
            <div class="text-[15px] leading-relaxed text-[#3c3938]">
                <slot />
            </div>

            <div class="mt-8 flex items-center justify-end gap-6">
                <button
                    type="button"
                    class="font-brand text-[15px] font-bold text-brand-navy underline underline-offset-2 transition hover:text-brand-red disabled:pointer-events-none disabled:opacity-40"
                    :disabled="busy"
                    @click="modalogStore.closeModalog()"
                >
                    {{ cancelLabel }}
                </button>
                <BrandButton
                    class="h-12 px-7 text-[15px] disabled:pointer-events-none disabled:opacity-40"
                    :variant="danger ? 'whiteOnOrange' : 'navy'"
                    :loading="busy"
                    :disabled="confirmDisabled"
                    @click="emit('confirm')"
                >
                    {{ busy && busyLabel ? busyLabel : confirmLabel }}
                </BrandButton>
            </div>

            <!-- Optional fine print under the footer row, e.g. the
                 "Already registered?" link on the guest confirm-purchase
                 modalog (GH-1539). -->
            <div
                v-if="$slots['footer-note']"
                class="mt-3 text-right text-[13px] text-[#3c3938]"
            >
                <slot name="footer-note" />
            </div>
        </BrandModalogShell>
    </div>
</template>
